import type { ClientConfigRepository } from '@/core/data/config/clientConfig.repository';
import type { LocationGambitDto } from '@/core/data/gambit/gambit.interface';
import type {
  AddLocationCommentData,
  AddLocationFormData,
  BaseLocationDto,
  CityDto,
  CommentDto,
  DetailLocationDto,
  GamPinObject,
  GeoCoords,
  GooglePlaceQuery,
  GooglePlaceResultDto,
  ImageDto,
  MapClusterData,
  MapLocationDto,
  SearchLocationDto,
  SearchLocationOptions,
} from '@/core/data/location/location.interface';
import { LocationType, MapLocationType } from '@/core/data/location/location.type';
import type { StorageRepository } from '@/core/data/storage/storage.repository';
import type { LocalizationService } from '@/core/domain/localization.service';
import { gambits as gambitsInstance } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import LocationHelper from '@/core/helpers/location.helper';
import { createLogger } from '@/core/helpers/logger.helper';
import { LocationApi } from '@/core/network/api/location/location.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HttpError } from '@/core/network/http/httpError';
import router from '@/router';
import { useMapStore } from '@/stores/map.store';
import { useWalkStore } from '@/stores/walk.store';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { Algorithm } from '@googlemaps/markerclusterer';
import { SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import type { SparkSegment } from '../spark/spark.interface';
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
// type AdvancedMarkerElementOptions = google.maps.marker.AdvancedMarkerElementOptions;

const logger = createLogger('LocationRepository');

export class LocationRepository {
  private readonly httpAPI: LocationApi;
  private readonly storage: StorageRepository;
  private readonly config: ClientConfigRepository;
  private readonly localizationService: LocalizationService;
  private pinElements: { [key: string]: GamPinObject } = {};
  private pinLocationData: { [key: string]: MapLocationDto } = {};
  private _onClickChessboard?: (item: MapLocationDto) => void;

  constructor(
    httpAPI: LocationApi,
    storage: StorageRepository,
    config: ClientConfigRepository,
    localizationService: LocalizationService,
  ) {
    this.httpAPI = httpAPI;
    this.storage = storage;
    this.config = config;
    this.localizationService = localizationService;
  }

  // async getCity(city: string, country: string, coordinates: GeoCoords): Promise<GamResponse<CityDto> | null> {
  //   try {
  //     const response = await this.httpAPI.getCity(city, country, coordinates);
  //     if (response.data?.data) {
  //       return {
  //         data: response.data.data,
  //         cursor: response.data.cursor,
  //       };
  //     } else {
  //       return null;
  //     }
  //   } catch (e) {
  //     const httpError = e as HttpError;
  //     logger.error(`${httpError.error.code} => ${httpError.error.message}`);
  //     throw httpError.error;
  //   }
  // }

  async getCityFor(coordinates: GeoCoords): Promise<GamResponse<CityDto> | null> {
    try {
      const response = await this.httpAPI.getCityFor(coordinates);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getLocations(
    listId: GamListId,
    location: GeoCoords,
    radius: number,
  ): Promise<GamResponse<BaseLocationDto[]> | null> {
    try {
      const response = await this.httpAPI.getLocations(listId, location, radius);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getLocationsByCoordinates(
    location: GeoCoords,
    options?: {
      distance?: number;
      perPage?: number;
      intentDate?: string;
      availabilityDate?: string;
    },
  ): Promise<GamResponse<BaseLocationDto[]> | null> {
    try {
      const response = await this.httpAPI.getLocationsByCoordinates(location, options);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getSparkableLocationsByCoordinates(
    location: GeoCoords,
    options?: {
      distance?: number;
      perPage?: number;
      intentDate?: string;
      availabilityDate?: string;
    },
  ): Promise<GamResponse<BaseLocationDto[]> | null> {
    try {
      const response = await this.httpAPI.getSparkableLocationsByCoordinates(location, options);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getAvailabilities(
    locationIds: string[],
    day: number,
    startTime: number,
    endTime: number,
  ): Promise<GamResponse<SparkSegment[]> | null> {
    try {
      const response = await this.httpAPI.getAvailabilities(locationIds, day, startTime, endTime);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getMapLocations(
    listId: GamListId,
    center: GeoCoords,
    radius: number,
  ): Promise<GamResponse<MapLocationDto[]> | null> {
    try {
      const response = await this.httpAPI.getMapLocations(listId, center, radius);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async searchLocationsByQuery(
    query?: string,
    options?: SearchLocationOptions,
  ): Promise<GamResponse<SearchLocationDto[]> | null> {
    try {
      const response = await this.httpAPI.searchLocationsByQuery(query, options);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async fetchGooglePlaceDetails(data: GooglePlaceQuery): Promise<GamResponse<GooglePlaceResultDto> | null> {
    try {
      const response = await this.httpAPI.fetchGooglePlaceDetails(data);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async reverseGeocode(lat: number, lng: number): Promise<GamResponse<SearchLocationDto> | null> {
    try {
      const response = await this.httpAPI.reverseGeocode(lat, lng);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getLocation(id: string): Promise<GamResponse<DetailLocationDto> | null> {
    try {
      const response = await this.httpAPI.getLocation(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getLocationCover(id: string): Promise<GamResponse<string> | null> {
    try {
      const response = await this.httpAPI.getLocationCover(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getLocationImages(id: string): Promise<GamResponse<ImageDto[]> | null> {
    try {
      const response = await this.httpAPI.getLocationImages(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  // async getLocationComments(id: string): Promise<GamResponse<CommentDto[]> | null> {
  //   try {
  //     const response = await this.httpAPI.getLocationComments(id);
  //     return response.data?.data ? { data: response.data.data } : null;
  //   } catch (e) {
  //     const httpError = e as HttpError;
  //     logger.error(`${httpError.error.code} => ${httpError.error.message}`);
  //     throw httpError.error;
  //   }
  // }

  async addNewLocation(data: AddLocationFormData): Promise<GamResponse<BaseLocationDto> | null> {
    try {
      const response = await this.httpAPI.addLocation(data);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createFirestoreDoc(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createFirestoreDoc(id);
      return response.status === 200 ? response.data || { data: response.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  // async updateLocation(data: AddLocationFormData): Promise<GamResponse<BaseLocationDto> | null> {
  //   try {
  //     const response = await this.httpAPI.updateLocation(data);
  //     return response.data?.data ? { data: response.data.data } : null;
  //   } catch (e) {
  //     const httpError = e as HttpError;
  //     logger.error(`${httpError.error.code} => ${httpError.error.message}`);
  //     throw httpError.error;
  //   }
  // }

  async addNewComment(id: string, data: AddLocationCommentData): Promise<GamResponse<CommentDto> | null> {
    try {
      const response = await this.httpAPI.addComment(id, data);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteComment(id: string): Promise<boolean> {
    try {
      const response = await this.httpAPI.deleteComment(id);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async toggleFavorite(id: string, favorite?: boolean): Promise<boolean> {
    try {
      const response = await this.httpAPI.toggleFavorite(id, favorite);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async toggleImageLike(id: string, like: boolean): Promise<boolean> {
    try {
      const response = await this.httpAPI.toggleImageLike(id, like);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async addPhoto(id: string, photo?: File | null | string): Promise<GamResponse<ImageDto> | null> {
    try {
      const response = await this.httpAPI.addPhoto(id, photo);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async pinCover(
    id: string,
    imageBlobHash: string | null,
  ): Promise<GamResponse<{ coverBlobHash: string | null }> | null> {
    try {
      const response = await this.httpAPI.pinCover(id, imageBlobHash);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteLocationImage(id: string): Promise<boolean> {
    try {
      const response = await this.httpAPI.deleteLocationImage(id);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  getClusterAlgorithm(): Algorithm {
    return new SuperClusterAlgorithm(this.config.parseClusterConfig());
  }

  getClusterPin(count: number): HTMLDivElement {
    const clusterPinWrapper: HTMLDivElement = document.createElement('div');
    clusterPinWrapper.classList.add('gam-pin-cluster-wrapper');
    clusterPinWrapper.innerHTML = `<div class="cluster-count">${count.toString()}</div>`;
    return clusterPinWrapper;
  }

  // @ts-ignore
  private markerElementClass: typeof AdvancedMarkerElement;

  async getBaseMarkers(data: MapClusterData): Promise<AdvancedMarkerElement[]> {
    if (!data.items) return [];
    if (!this.markerElementClass) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      this.markerElementClass = AdvancedMarkerElement;
    }

    const selectedId = data.activeSpotId || String(router.currentRoute.value.query.spot || '');

    if (data.onClickChessboard) {
      this._onClickChessboard = data.onClickChessboard;
    }

    return data.items.map((item) => {
      const pinObject = this.getLocationPin(item, selectedId, this._onClickChessboard, data.dotMode);
      this.pinElements[item.id] = pinObject;
      this.pinLocationData[item.id] = item;
      // const payload: AdvancedMarkerElementOptions = {
      //   position: pinObject.coordinates,
      //   content: pinObject.element,
      //   // @ts-ignore
      //   anchorPoint: 'CENTER',
      // };

      const marker = new this.markerElementClass({
        position: pinObject.coordinates,
        content: pinObject.element,
        zIndex: this.getMarkerZIndex(item, selectedId, data.dotMode),
      });

      marker.id = item.id;

      if (typeof data.onClickMarker === 'function') {
        marker.addListener('gmp-click', () => data.onClickMarker?.(item));
      }

      return marker;
    });
  }

  getSelectedPin(id: string): GamPinObject {
    return this.pinElements[id];
  }

  updateSelectedPin(location: MapLocationDto, selected?: string): GamPinObject {
    this.pinElements[location.id] = this.getLocationPin(location, selected, this._onClickChessboard);
    this.pinLocationData[location.id] = location;
    return this.pinElements[location.id];
  }

  getPinLocationData(id: string): MapLocationDto | undefined {
    return this.pinLocationData[id];
  }

  clearMapPins(): void {
    this.pinElements = {};
    this.pinLocationData = {};
  }

  // TODO: Move to gambit-related module
  private isGambitLive(gambit?: LocationGambitDto | null): boolean {
    if (gambit) {
      const dateType = GambitHelper.getGambitsDateType(gambit.start, false, gambit.end);
      return dateType === GamDateType.NOW;
    }
    return false;
  }

  // TODO: Move to gambit-related module
  private isGambitSoon(gambit?: LocationGambitDto | null): boolean {
    if (gambit) {
      const dateType = GambitHelper.getGambitsDateType(gambit.start, false, gambit.end);
      return dateType === GamDateType.SOON;
    }
    return false;
  }

  // TODO: Move to gambit-related module
  private isGambitToday(gambit?: LocationGambitDto | null): boolean {
    if (gambit) {
      const dateType = GambitHelper.getGambitsDateType(gambit.start, false, gambit.end);
      return dateType === GamDateType.TODAY;
    }
    return false;
  }

  // TODO: Move to gambit-related module
  private isGambitTomorrow(gambit?: LocationGambitDto | null): boolean {
    if (gambit) {
      const dateType = GambitHelper.getGambitsDateType(gambit.start, false, gambit.end);
      return dateType === GamDateType.TOMORROW;
    }
    return false;
  }

  /** Selected while the overlay is open, or still ringed after it closed. */
  private pinShowsSelectedRing(itemId: string, selected?: string | null): boolean {
    if (selected && selected === itemId) return true;
    const openSpot = router.currentRoute.value.query.spot;
    if (typeof openSpot === 'string' && openSpot === itemId) return true;
    return useMapStore().ringedSpotId === itemId;
  }

  getMarkerZIndex(item: MapLocationDto, selected?: string | null, dotMode?: boolean): number {
    if (this.pinShowsSelectedRing(item.id, selected)) return 20;

    const hasCheckIns = Number(item.visitorCount) > 0;
    const hasGambits = Number(item.nextGambits?.length) > 0;
    const hasBoards = Number(item.activeChessboardCount) > 0;
    const hasIrlActivity = hasCheckIns || hasGambits;
    const walkStore = useWalkStore();
    const isWalkSpot = walkStore.startSpot?.id === item.id || walkStore.revealSpot?.id === item.id;
    const isBoardOnlyDot = hasBoards && !hasIrlActivity && !isWalkSpot && !useMapStore().showAllSpots;
    const isActive = hasIrlActivity || isWalkSpot || (hasBoards && !isBoardOnlyDot);

    if (dotMode && !isWalkSpot) return 1;
    if (isActive) return 10;
    return 1;
  }

  private getLocationPin(
    item: MapLocationDto,
    selected?: string | null,
    onClickChessboard?: (item: MapLocationDto) => void,
    dotMode?: boolean,
  ): GamPinObject {
    const hasCheckIns = Number(item.visitorCount) > 0;
    const hasGambits = Number(item.nextGambits?.length) > 0;
    const hasBoards = Number(item.activeChessboardCount) > 0;
    const hasIrlActivity = hasCheckIns || hasGambits;
    const isBoardOnly = hasBoards && !hasIrlActivity;
    const isEngaged = item.isVetted || item.isSparked || item.favorite;

    const walkStore = useWalkStore();
    const isWalkStart = walkStore.startSpot?.id === item.id;
    const isWalkReveal = walkStore.revealSpot?.id === item.id;
    const isWalkSpot = isWalkStart || isWalkReveal;
    const hasActivity = hasIrlActivity || isWalkSpot;

    const mapStore = useMapStore();
    const isBoardOnlyDot = isBoardOnly && !isWalkSpot && !mapStore.showAllSpots;

    const gambit = item.nextGambits?.[0];
    const gambitDateType = gambit ? GambitHelper.getGambitsDateType(gambit.start, false, gambit.end) : null;
    const isLive = hasCheckIns || gambitDateType === GamDateType.NOW || gambitDateType === GamDateType.SOON;

    const wrapper: HTMLDivElement = document.createElement('div');
    wrapper.id = `pin-${item.id}`;
    wrapper.classList.add('location-circle');
    if (dotMode && !isWalkSpot) wrapper.classList.add('lc-dot');
    if (isBoardOnlyDot) wrapper.classList.add('lc-dot', 'lc-board-only');
    if (item.isSparked) wrapper.classList.add('lc-sparked');
    if (hasActivity || (isBoardOnly && !isBoardOnlyDot)) wrapper.classList.add('lc-active');
    if (hasCheckIns) wrapper.classList.add('lc-checkin');
    if (hasGambits) wrapper.classList.add('lc-irl');
    if (hasBoards && item.hasPlayableBoard !== false) wrapper.classList.add('lc-board');
    if (isLive) wrapper.classList.add('lc-live');
    if (this.pinShowsSelectedRing(item.id, selected)) wrapper.classList.add('lc-selected');
    if (item.isVetted || hasBoards) wrapper.classList.add('lc-no-plant');
    if (isEngaged) wrapper.classList.add('lc-engaged');
    if (isWalkSpot) wrapper.classList.add('lc-walk-spot');

    let floatingBoard: HTMLDivElement | null = null;
    if (hasBoards) {
      floatingBoard = document.createElement('div');
      floatingBoard.classList.add('lc-floating-board');
      if (item.hasPlayableBoard === false) {
        floatingBoard.classList.add('lc-board-gray');
      }
      const boardIcon: HTMLDivElement = document.createElement('div');
      boardIcon.classList.add('lc-board-icon');
      floatingBoard.appendChild(boardIcon);
      if (onClickChessboard) {
        floatingBoard.addEventListener('click', (e) => {
          e.stopPropagation();
          onClickChessboard(item);
        });
      }
      if (!isBoardOnlyDot) {
        wrapper.appendChild(floatingBoard);
      }
    }

    const ring: HTMLDivElement = document.createElement('div');
    ring.classList.add('lc-ring');

    const main: HTMLDivElement = document.createElement('div');
    main.classList.add('lc-main');

    if (item.coverBlobHash) {
      const img: HTMLImageElement = document.createElement('img');
      img.src = gambitsInstance.imageService.getImageSrc(item.coverBlobHash, 112);
      img.loading = 'lazy';
      main.appendChild(img);
    } else if (item.mapLocationType === MapLocationType.LOCATION) {
      const iconDiv: HTMLDivElement = document.createElement('div');
      iconDiv.classList.add('lc-type-icon');
      if (item.private) iconDiv.classList.add('private');
      else if (item.favorite) iconDiv.classList.add('favorite');
      else iconDiv.classList.add(LocationHelper.getLocationTypeIcon(item.type || LocationType.CHESS_FRIENDLY));
      main.appendChild(iconDiv);
    }

    ring.appendChild(main);

    if (isWalkSpot) {
      const walkLabel: HTMLDivElement = document.createElement('div');
      walkLabel.classList.add('lc-reveal-label');
      const sq: HTMLSpanElement = document.createElement('span');
      sq.classList.add(isWalkStart ? 'lc-start-square' : 'lc-reveal-square');
      walkLabel.appendChild(sq);
      ring.appendChild(walkLabel);
    }

    if (hasCheckIns) {
      const badge: HTMLDivElement = document.createElement('div');
      badge.classList.add('lc-badge', 'lc-badge-tr');
      const badgeIcon: HTMLDivElement = document.createElement('div');
      badgeIcon.classList.add('lc-badge-icon', 'pawn');
      badge.appendChild(badgeIcon);
      ring.appendChild(badge);
    }

    if (hasGambits) {
      const badge: HTMLDivElement = document.createElement('div');
      badge.classList.add('lc-badge', 'lc-badge-br');
      const badgeIcon: HTMLDivElement = document.createElement('div');
      badgeIcon.classList.add('lc-badge-icon', 'play');
      badge.appendChild(badgeIcon);
      ring.appendChild(badge);
    }

    if (isBoardOnlyDot && floatingBoard) {
      ring.appendChild(floatingBoard);
    }

    wrapper.appendChild(ring);

    if (item.name) {
      const nameLabel: HTMLDivElement = document.createElement('div');
      nameLabel.classList.add('lc-name');
      nameLabel.innerHTML = `<span>${item.name}</span>`;
      wrapper.appendChild(nameLabel);
    }

    if (item.address) {
      const addressLabel: HTMLDivElement = document.createElement('div');
      addressLabel.classList.add('lc-address');
      addressLabel.innerHTML = `<span>${item.address}</span>`;
      wrapper.appendChild(addressLabel);
    }

    let temporalKey: string | null = null;
    if (gambitDateType === GamDateType.NOW) temporalKey = 'live';
    else if (gambitDateType === GamDateType.SOON) temporalKey = 'soon';
    else if (gambitDateType === GamDateType.TODAY) temporalKey = 'today';
    else if (gambitDateType === GamDateType.TOMORROW) temporalKey = 'tomorrow';

    if (temporalKey) {
      const temporal: HTMLDivElement = document.createElement('div');
      temporal.classList.add('lc-temporal');
      temporal.innerHTML = `<span>${this.localizationService.localize(`app.component.map.pin.${temporalKey}`)}</span>`;
      wrapper.appendChild(temporal);
    }

    return {
      element: wrapper,
      coordinates: item.coordinates,
    };
  }

  async checkIn(id: string, options?: { gambitId?: string; ignoreGambit?: boolean }): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.checkIn(id, options);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateCheckIn(id: string, data: { chessSetCount?: number }): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.updateCheckIn(id, data);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async checkOut(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.checkOut(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }
}
